import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, useFetch, StatusPill, Modal, Field, TextInput, Textarea, Select, PrimaryButton } from "../../components/crm/ui";
import { Check, X, Phone, Mail, MapPin, Pencil, ShieldCheck, ShieldAlert } from "lucide-react";
import { INDIAN_STATES } from "../../lib/indianStates";

const STATUS_TONE = { active: "success", pending_approval: "warning", suspended: "danger" };

export default function SolarConsultants() {
  const { data: scs, reload } = useFetch("/admin/sc");
  const [filter, setFilter] = useState("all");
  const [editSc, setEditSc] = useState(null);

  async function approve(u) {
    try {
      await api.put(`/admin/sc/${u.id}/approve`);
      toast.success(`${u.name} approved`);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }
  async function suspend(u) {
    if (!window.confirm(`Suspend ${u.name}? They will be unable to log in.`)) return;
    try {
      await api.put(`/admin/sc/${u.id}/suspend`);
      toast.success(`${u.name} suspended`);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }

  const filtered = filter === "all" ? scs : scs.filter((u) => (u.status || "active") === filter);
  const counts = scs.reduce((acc, u) => { const s = u.status || "active"; acc[s] = (acc[s] || 0) + 1; return acc; }, {});

  return (
    <div data-testid="sc-admin-page">
      <PageHeader overline="Network" title="Solar Consultants" />

      <div className="bg-white border border-[#DDE5D8]">
        <div className="px-4 py-3 border-b border-[#DDE5D8] flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#475569] mr-1">Status</span>
          {[
            { v: "all", l: "All", c: scs.length },
            { v: "pending_approval", l: "Pending approval", c: counts.pending_approval || 0 },
            { v: "active", l: "Active", c: counts.active || 0 },
            { v: "suspended", l: "Suspended", c: counts.suspended || 0 },
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setFilter(f.v)}
              data-testid={`filter-${f.v}`}
              className={"text-[11px] font-mono uppercase tracking-widest px-2.5 py-1.5 border " +
                (filter === f.v ? "bg-[#0F1A12] text-white border-[#0F1A12]" : "border-[#DDE5D8] text-[#475569] hover:border-[#1F4E2C]")}
            >
              {f.l} <span className="ml-1 opacity-60">({f.c})</span>
            </button>
          ))}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="sc-admin-table">
            <thead className="bg-[#F4F6F1] border-b border-[#DDE5D8]">
              <tr>
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">Consultant</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">Contact</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">City · PIN · UPI</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">Region</th>
                <th className="px-4 py-3 text-right font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">Leads</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">Status</th>
                <th className="px-4 py-3 text-right font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const status = u.status || "active";
                return (
                  <tr
                    key={u.id}
                    data-testid={`sc-row-${u.id}`}
                    className="border-b border-[#EEF2EA] hover:bg-[#F4F6F1] cursor-pointer"
                    onClick={() => setEditSc(u)}
                    role="button"
                  >
                    <td className="px-4 py-3">
                      <div className="font-display font-semibold flex items-center gap-1.5">
                        {u.name}
                        {u.phone_verified && <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" title="Phone OTP-verified" />}
                      </div>
                      <div className="font-mono text-[10px] text-[#94A3B8] mt-0.5">Joined {new Date(u.created_at).toLocaleDateString("en-IN")}</div>
                    </td>
                    <td className="px-4 py-3 text-xs" onClick={(e) => e.stopPropagation()}>
                      {u.email && <a href={`mailto:${u.email}`} className="flex items-center gap-1 text-[#0F1A12] hover:text-[#1F4E2C] truncate max-w-[220px]"><Mail className="w-3 h-3 flex-shrink-0" /> <span className="truncate">{u.email}</span></a>}
                      {u.phone && <a href={`tel:${u.phone}`} className="flex items-center gap-1 text-[#475569] mt-1 hover:text-[#1F4E2C]"><Phone className="w-3 h-3" /> {u.phone}</a>}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono">
                      {u.city && <div className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {u.city}</div>}
                      {u.pin_code && <div className="text-[#475569] mt-1">PIN: {u.pin_code}</div>}
                      {u.upi_id && <div className="text-[#94A3B8] mt-1">UPI: {u.upi_id}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono" data-testid={`sc-region-${u.id}`}>
                      {u.region
                        ? <span className={u.pin_code ? "text-[#1F4E2C] font-semibold" : "text-[#94A3B8]"}>{u.region}</span>
                        : <span className="text-[#94A3B8]">— Unassigned —</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm font-semibold">{u.lead_count || 0}</td>
                    <td className="px-4 py-3"><StatusPill value={status.replace("_", " ")} tone={STATUS_TONE[status] || "info"} /></td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => setEditSc(u)}
                          data-testid={`edit-${u.id}`}
                          className="p-1.5 border border-[#DDE5D8] text-[#475569] hover:border-[#1F4E2C] hover:text-[#1F4E2C]"
                          title="Edit"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                        {status === "pending_approval" && (
                          <button onClick={() => approve(u)} data-testid={`approve-${u.id}`}
                            className="text-[10px] font-mono uppercase tracking-wider bg-[#1F4E2C] text-white px-2.5 py-1 hover:bg-[#0F1A12] inline-flex items-center gap-1">
                            <Check className="w-3 h-3" /> Approve
                          </button>
                        )}
                        {status === "active" && (
                          <button onClick={() => suspend(u)} data-testid={`suspend-${u.id}`}
                            className="text-[10px] font-mono uppercase tracking-wider bg-white border border-[#DDE5D8] text-[#475569] px-2.5 py-1 hover:text-red-600 hover:border-red-600 inline-flex items-center gap-1">
                            <ShieldAlert className="w-3 h-3" /> Suspend
                          </button>
                        )}
                        {status === "suspended" && (
                          <button onClick={() => approve(u)} data-testid={`reactivate-${u.id}`}
                            className="text-[10px] font-mono uppercase tracking-wider bg-[#1F4E2C] text-white px-2.5 py-1 hover:bg-[#0F1A12]">
                            Reactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-sm font-mono text-[#94A3B8]">No consultants in this bucket.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <EditScModal sc={editSc} onClose={() => setEditSc(null)} onSaved={() => { setEditSc(null); reload(); }} />
    </div>
  );
}

function EditScModal({ sc, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (sc) {
      setForm({
        id: sc.id,
        name: sc.name || "",
        email: sc.email || "",
        phone: sc.phone || "",
        city: sc.city || "",
        state: sc.state || "",
        pin_code: sc.pin_code || "",
        upi_id: sc.upi_id || "",
        notes: sc.notes || "",
        status: sc.status || "active",
      });
    } else {
      setForm(null);
    }
  }, [sc]);

  if (!sc || !form) return null;

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        city: form.city || null,
        state: form.state || null,
        pin_code: form.pin_code,
        upi_id: form.upi_id || null,
        notes: form.notes || null,
        status: form.status,
      };
      await api.put(`/admin/sc/${sc.id}`, payload);
      toast.success("Consultant updated");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  }

  function up(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  return (
    <Modal open={!!sc} onClose={() => { setForm(null); onClose?.(); }} title={`Edit · ${sc.name}`} wide>
      <form onSubmit={save} className="space-y-4" data-testid="sc-edit-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Name *">
            <TextInput required value={form.name} onChange={(e) => up("name", e.target.value)} data-testid="sc-edit-name" />
          </Field>
          <Field label="Status *">
            <Select value={form.status} onChange={(e) => up("status", e.target.value)} data-testid="sc-edit-status">
              <option value="active">Active</option>
              <option value="pending_approval">Pending approval</option>
              <option value="suspended">Suspended</option>
            </Select>
          </Field>
          <Field label="Email">
            <TextInput type="email" value={form.email} onChange={(e) => up("email", e.target.value)} data-testid="sc-edit-email" />
          </Field>
          <Field label="Phone (E.164)">
            <TextInput value={form.phone} onChange={(e) => up("phone", e.target.value)} data-testid="sc-edit-phone" />
          </Field>
          <Field label="City">
            <TextInput value={form.city} onChange={(e) => up("city", e.target.value)} data-testid="sc-edit-city" />
          </Field>
          <Field label="PIN Code (sets Region)">
            <TextInput value={form.pin_code} onChange={(e) => up("pin_code", e.target.value.replace(/\D/g, "").slice(0, 6))} maxLength={6} placeholder="6-digit PIN" data-testid="sc-edit-pincode" />
          </Field>
          <Field label="State (fallback, used only until a PIN code is set)">
            <Select value={form.state} onChange={(e) => up("state", e.target.value)} data-testid="sc-edit-state">
              <option value="">— Not set —</option>
              {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </Field>
          <Field label="UPI ID">
            <TextInput value={form.upi_id} onChange={(e) => up("upi_id", e.target.value)} data-testid="sc-edit-upi" />
          </Field>
        </div>
        <Field label="Admin notes (internal)">
          <Textarea rows={3} value={form.notes} onChange={(e) => up("notes", e.target.value)} data-testid="sc-edit-notes" />
        </Field>

        <div className="flex items-center justify-between pt-3 border-t border-[#DDE5D8]">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8]">
            {sc.phone_verified ? "Phone verified via OTP" : "Phone NOT verified"} · {sc.lead_count || 0} leads referred
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => { setForm(null); onClose?.(); }} className="text-xs font-mono uppercase tracking-widest px-3 py-1.5 border border-[#DDE5D8] text-[#475569] hover:border-[#1F4E2C] hover:text-[#1F4E2C]">Cancel</button>
            <PrimaryButton type="submit" disabled={saving} testid="sc-edit-save">{saving ? "Saving…" : "Save changes"}</PrimaryButton>
          </div>
        </div>
      </form>
    </Modal>
  );
}
